import React, { useMemo, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { Send } from 'lucide-react'
import { useBillingEntityLocations, useSharePaymentLink } from '@data/billing/billing'

const DEFAULT_MESSAGE =
  'Please find the payment link for our Smart Flow subscription renewal.'

interface Props {
  billingEntityId: number
  show: boolean
  onHide: () => void
}

const SharePaymentLinkModal: React.FC<Props> = ({ billingEntityId, show, onHide }) => {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState(DEFAULT_MESSAGE)
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set())

  const { data: locations, isLoading: loadingLocations } = useBillingEntityLocations(
    show ? billingEntityId : undefined,
  )
  const { mutateAsync: shareLink, isPending } = useSharePaymentLink()

  const allSelected = useMemo(
    () =>
      !!locations &&
      locations.length > 0 &&
      locations.every((l) => selectedLocationIds.has(l.id)),
    [locations, selectedLocationIds],
  )

  /**
   * The locations endpoint returns every location on the account, so a
   * selection can span several billing entities — each is billed by a
   * different Stripe customer. Group the selection by owning entity and send
   * one request per entity rather than one per location, so each email is
   * addressed to the right customer and covers all of that customer's
   * selected locations.
   *
   * A location with no billing entity assigned is skipped: there is no Stripe
   * customer to bill it to, and attaching it to the opened entity would bill
   * the wrong one.
   */
  const selectedGroups = useMemo(() => {
    const map = new Map<number, number[]>()
    for (const loc of locations ?? []) {
      if (!selectedLocationIds.has(loc.id)) continue
      if (loc.billing_entity_id === null) continue
      const ids = map.get(loc.billing_entity_id)
      if (ids) ids.push(loc.id)
      else map.set(loc.billing_entity_id, [loc.id])
    }
    return Array.from(map.entries()).map(([entityId, locationIds]) => ({
      billingEntityId: entityId,
      locationIds,
    }))
  }, [locations, selectedLocationIds])

  const coveredCount = useMemo(
    () => selectedGroups.reduce((n, g) => n + g.locationIds.length, 0),
    [selectedGroups],
  )

  const skippedCount = useMemo(
    () =>
      (locations ?? []).filter(
        (l) => selectedLocationIds.has(l.id) && l.billing_entity_id === null,
      ).length,
    [locations, selectedLocationIds],
  )

  const toggleAll = () => {
    if (!locations) return
    setSelectedLocationIds(allSelected ? new Set() : new Set(locations.map((l) => l.id)))
  }

  const toggleOne = (id: number) => {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const reset = () => {
    setEmail('')
    setMessage(DEFAULT_MESSAGE)
    setSelectedLocationIds(new Set())
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payloads =
      selectedGroups.length === 0
        ? [{ billing_entity_id: billingEntityId, email, message }]
        : selectedGroups.map((group) => ({
            billing_entity_id: group.billingEntityId,
            email,
            message,
            location_ids: group.locationIds,
          }))

    // Fire all requests in parallel. useSharePaymentLink already toasts on
    // success/failure per call.
    await Promise.allSettled(payloads.map((p) => shareLink(p)))
    reset()
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="text-uppercase">Share Payment Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Send a secure, Stripe-hosted payment link directly to your finance or
            accounts team.
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="text-muted small fw-semibold text-uppercase">
              Accounts Email Address
            </Form.Label>
            <Form.Control
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="accounts@example.com"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-muted small fw-semibold text-uppercase">
              Message (Optional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional message included in the email body..."
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="text-muted small fw-semibold text-uppercase">
              Select Locations (Optional)
            </Form.Label>
            {loadingLocations ? (
              <div className="text-muted small">
                <Spinner size="sm" animation="border" /> Loading locations…
              </div>
            ) : !locations || locations.length === 0 ? (
              <div className="text-muted small">No locations on this billing entity.</div>
            ) : (
              <>
                <Form.Check
                  type="checkbox"
                  id="share-payment-link-select-all"
                  className="fw-semibold mb-1"
                  label="Select All"
                  checked={allSelected}
                  onChange={toggleAll}
                />
                <div
                  className="border rounded px-3 py-2"
                  style={{ maxHeight: 180, overflowY: 'auto' }}
                >
                  {locations.map((loc) => (
                    <Form.Check
                      key={loc.id}
                      type="checkbox"
                      id={`share-payment-link-loc-${loc.id}`}
                      label={
                        loc.billing_entity_id === null ? (
                          <>
                            {loc.location_name}{' '}
                            <span className="text-muted small">(no billing entity)</span>
                          </>
                        ) : (
                          loc.location_name
                        )
                      }
                      checked={selectedLocationIds.has(loc.id)}
                      onChange={() => toggleOne(loc.id)}
                    />
                  ))}
                </div>
                {(selectedGroups.length > 0 || skippedCount > 0) && (
                  <Form.Text className="text-muted d-block">
                    {selectedGroups.length === 1
                      ? `1 email will be sent, covering ${coveredCount} location${
                          coveredCount === 1 ? '' : 's'
                        }.`
                      : selectedGroups.length > 1
                        ? `${selectedGroups.length} separate emails will be sent — one per billing entity, covering ${coveredCount} locations.`
                        : ''}
                    {skippedCount > 0 && (
                      <span className="d-block">
                        {skippedCount} selected location
                        {skippedCount === 1 ? ' has' : 's have'} no billing entity and
                        will be skipped.
                      </span>
                    )}
                  </Form.Text>
                )}
              </>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                Send link <Send size={14} className="ms-1" />
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default SharePaymentLinkModal
